import React, { useCallback, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { Button, HStack, Icon, Modal, Text, VStack } from 'govwell-ui'
import { AIChatTopQuestion } from 'types/graphql'

import { useHideAIChatFromInsightsMutation } from 'src/fetch/aiChat'
import useDisclosure from 'src/hooks/use-disclosure'
import ParsedHtmlMessage from 'src/pages/AIChat/AIResponseMessage/ParsedHtmlMessage'
import { QueryKey } from 'src/utils/queryClient'

const DISCLAIMER_PATTERN = /<em[^>]*>This\s+is\s+an\s+AI-generated[\s\S]*?<\/em>/gi
const BUTTON_ROW_PATTERN = /<ButtonRow>[\s\S]*?<\/ButtonRow>/gi

const cleanHtmlForDisplay = (html: string): string => {
  return html.replace(BUTTON_ROW_PATTERN, '').replace(DISCLAIMER_PATTERN, '')
}

type Props = {
  topQuestion: AIChatTopQuestion
}

const QuestionCard = ({ topQuestion }: Props) => {
  const modalState = useDisclosure()
  const confirmModalState = useDisclosure()
  const queryClient = useQueryClient()
  const { mutateAsync: hideAIChatFromInsights } = useHideAIChatFromInsightsMutation()
  const [isHiding, setIsHiding] = useState(false)

  const handleClick = useCallback(() => {
    modalState.open()
  }, [modalState])

  const handleHideClick = useCallback(() => {
    confirmModalState.open()
  }, [confirmModalState])

  const handleConfirmHide = useCallback(async () => {
    setIsHiding(true)
    try {
      await hideAIChatFromInsights({ input: { aiChatId: topQuestion.id } })
      await queryClient.invalidateQueries({ queryKey: [QueryKey.AIChatTopQuestions] })
      confirmModalState.close()
      modalState.close()
    } catch {
      notification.error({ message: 'Failed to hide question. Please try again.' })
    } finally {
      setIsHiding(false)
    }
  }, [confirmModalState, hideAIChatFromInsights, modalState, queryClient, topQuestion.id])

  const pageSourceLabel = useMemo(() => {
    if (topQuestion.pageSource === 'ApplyForm') {
      return 'Asked while on a form'
    }
    if (topQuestion.pageSource === 'RecordDetail') {
      return 'Asked while viewing a record'
    }
    return null
  }, [topQuestion.pageSource])

  const cleanedHtml = useMemo(
    () => cleanHtmlForDisplay(topQuestion.response),
    [topQuestion.response]
  )

  return (
    <>
      <button
        type="button"
        className="flex h-full min-h-[340px] flex-1 flex-col rounded-xl border border-solid border-gray-200 bg-white text-left transition-shadow hover:shadow-md"
        onClick={handleClick}
      >
        {/* Plain divs used instead of govwell-ui Flex/VStack because their default
            width: 100% from styled-components conflicts with the parent flex layout,
            causing children to not stretch correctly */}
        <div className="flex h-full w-full flex-col">
          <div className="rounded-t-xl bg-emerald-50/60 p-4 pb-3">
            <Text.Body className="font-semibold text-gray-800">
              &ldquo;{topQuestion.question}&rdquo;
            </Text.Body>
          </div>
          <div className="relative flex-1 overflow-hidden p-4 pt-3">
            <div className="max-h-[216px] overflow-hidden text-sm text-gray-600">
              <ParsedHtmlMessage html={cleanedHtml} />
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 h-10 w-full bg-gradient-to-t from-white to-transparent" />
          </div>
          <HStack
            align="center"
            className="mt-auto w-full rounded-b-xl border-t border-gray-100 px-4 py-2.5"
          >
            <HStack gap={3} align="center" className="min-w-0">
              {topQuestion.citationCount > 0 && (
                <HStack gap={1} align="center" className="min-w-0">
                  <Icon icon="FaBookOpen" className="shrink-0 text-xs text-emerald-500" />
                  <Text.BodySmall className="truncate text-xs text-emerald-600">
                    {topQuestion.citationCount}{' '}
                    {topQuestion.citationCount === 1 ? 'source' : 'sources'} cited
                  </Text.BodySmall>
                </HStack>
              )}
              {pageSourceLabel && (
                <HStack gap={1} align="center" className="min-w-0">
                  <Icon icon="FaLocationDot" className="shrink-0 text-xs text-violet-500" />
                  <Text.BodySmall className="truncate text-xs text-violet-600">
                    {pageSourceLabel}
                  </Text.BodySmall>
                </HStack>
              )}
            </HStack>
            <Text.BodySmall className="ml-auto shrink-0 text-xs whitespace-nowrap text-gray-400">
              Read more
            </Text.BodySmall>
          </HStack>
        </div>
      </button>
      {modalState.isOpen && (
        <Modal onClose={modalState.close} width="40vw">
          <Modal.Header>Notable Question</Modal.Header>
          <Modal.Content>
            <VStack gap={4} className="max-h-[60vh] overflow-y-auto">
              <div className="rounded-lg bg-emerald-50/60 p-4">
                <Text.BodyLarge className="font-bold text-gray-900">
                  &ldquo;{topQuestion.question}&rdquo;
                </Text.BodyLarge>
                {pageSourceLabel && (
                  <HStack gap={1} align="center" className="mt-1">
                    <Icon icon="FaLocationDot" className="text-xs text-violet-500" />
                    <Text.BodySmall className="text-xs text-violet-600">
                      {pageSourceLabel}
                    </Text.BodySmall>
                  </HStack>
                )}
              </div>
              <ParsedHtmlMessage html={cleanedHtml} className="px-4 text-gray-700" />
              {topQuestion.citationCount > 0 && (
                <HStack gap={1} align="center" className="border-t border-gray-100 pt-3">
                  <Icon icon="FaBookOpen" className="text-sm text-emerald-500" />
                  <Text.BodySmall className="text-emerald-600">
                    {topQuestion.citationCount}{' '}
                    {topQuestion.citationCount === 1 ? 'source' : 'sources'} cited in this response
                  </Text.BodySmall>
                </HStack>
              )}
            </VStack>
          </Modal.Content>
          <Modal.Footer>
            <Button variant="default" onClick={handleHideClick}>
              Hide
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {confirmModalState.isOpen && (
        <Modal onClose={confirmModalState.close} width="400px">
          <Modal.Header>Hide Question</Modal.Header>
          <Modal.Content>
            <Text.Body>
              Are you sure you want to hide this question? It will no longer appear in Notable
              Questions.
            </Text.Body>
          </Modal.Content>
          <Modal.Footer>
            <HStack gap={2} justify="end">
              <Button variant="default" onClick={confirmModalState.close}>
                Cancel
              </Button>
              <Button variant="primary" isLoading={isHiding} onClick={handleConfirmHide}>
                Confirm
              </Button>
            </HStack>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default React.memo(QuestionCard)
